import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FunnelLayout } from '../../components/FunnelLayout';
import { BreedPhoto } from '../../components/funnel/BreedPhoto';
import { FunnelPillButton } from '../../components/funnel/FunnelPillButton';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { AgeCategory, Protein } from '../../types';
import { getAvailableRecipes } from '../../utils/products';
import { Search } from 'lucide-react';

type GateAnswer = 'si' | 'no';

interface ProteinOption {
  key: string;
  value: Protein | string;
  label: string;
  emoji: string;
  icon?: string;
}

const COMMON_PROTEINS: readonly ProteinOption[] = [
  {
    key: 'ternera',
    value: Protein.TERNERA,
    label: 'Ternera',
    emoji: '🐄',
    icon: '/images/iconos/alergias/ternera.png',
  },
  { key: 'pollo', value: Protein.POLLO, label: 'Pollo', emoji: '🐔', icon: '/images/iconos/alergias/pollo.png' },
  {
    key: 'cordero',
    value: Protein.CORDERO,
    label: 'Cordero',
    emoji: '🐑',
    icon: '/images/iconos/alergias/cordero.png',
  },
  { key: 'pavo', value: Protein.PAVO, label: 'Pavo', emoji: '🦃', icon: '/images/iconos/alergias/pavo.png' },
  { key: 'cerdo', value: Protein.CERDO, label: 'Cerdo', emoji: '🐖', icon: '/images/iconos/alergias/cerdo.png' },
  { key: 'salmon', value: Protein.PESCADO, label: 'Salmón', emoji: '🐟', icon: '/images/iconos/alergias/pescado.png' },
] as const;

interface ExtendedAllergen {
  key: string;
  value: Protein | string;
  label: string;
  category: 'proteinas' | 'vegetales';
}

const EXTENDED_ALLERGENS: readonly ExtendedAllergen[] = [
  { key: 'otros_pescados', value: Protein.PESCADO, label: 'Otros pescados', category: 'proteinas' },
  { key: 'pato', value: Protein.PATO, label: 'Pato', category: 'proteinas' },
  { key: 'conejo', value: Protein.CONEJO, label: 'Conejo', category: 'proteinas' },
  { key: 'huevo', value: 'huevo', label: 'Huevo', category: 'proteinas' },
  { key: 'mariscos', value: 'marisco_moluscos', label: 'Mariscos / moluscos', category: 'proteinas' },
  { key: 'algas', value: 'algas', label: 'Algas', category: 'vegetales' },
  { key: 'soja', value: 'soja', label: 'Soja', category: 'vegetales' },
  { key: 'gluten', value: 'gluten', label: 'Gluten / Cereales', category: 'vegetales' },
  { key: 'lacteos', value: 'lacteos', label: 'Lácteos', category: 'vegetales' },
  { key: 'arroz', value: 'arroz', label: 'Arroz', category: 'vegetales' },
  { key: 'maiz', value: 'maiz', label: 'Maíz', category: 'vegetales' },
  { key: 'patata', value: 'patata', label: 'Patata', category: 'vegetales' },
  { key: 'boniato', value: 'boniato', label: 'Boniato', category: 'vegetales' },
  { key: 'guisantes', value: 'guisantes', label: 'Guisantes', category: 'vegetales' },
] as const;

type AnyOption = ProteinOption | ExtendedAllergen;

const ALL_OPTIONS: readonly AnyOption[] = [...COMMON_PROTEINS, ...EXTENDED_ALLERGENS];

function findOptionByKey(key: string): AnyOption | undefined {
  return ALL_OPTIONS.find((o) => o.key === key);
}

function buildInitialKeys(allergies: ReadonlyArray<Protein | string>, savedKeys?: ReadonlyArray<string>): Set<string> {
  const validSavedKeys = (savedKeys ?? []).filter((key) => findOptionByKey(key));
  if (validSavedKeys.length > 0) return new Set(validSavedKeys);
  if (allergies.length === 0 || allergies.includes(Protein.NINGUNA)) return new Set();
  const keys = new Set<string>();
  allergies.forEach((p) => {
    const match = ALL_OPTIONS.find((o) => o.value === p);
    if (match) keys.add(match.key);
  });
  return keys;
}

function deriveAllergiesFromKeys(keys: Set<string>): Array<Protein | string> {
  const values = new Set<Protein | string>();
  keys.forEach((k) => {
    const opt = findOptionByKey(k);
    if (opt) values.add(opt.value);
  });
  return values.size > 0 ? Array.from(values) : [Protein.NINGUNA];
}

const P7Alergias: React.FC = () => {
  const { formData, updateFormData, goToNextStep, goToPreviousStep } = useForm();
  const { t, recipes, config } = useAppConfig();

  const nombre = formData.nombre || 'tu perro';

  const [showOtros, setShowOtros] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [attempted, setAttempted] = useState(false);

  /**
   * Gate state: null = initial / not chosen yet.
   * Lazy initializer for back-nav: if formData has any allergy other than
   * NINGUNA, the user must have previously answered 'si'.
   */
  const [gate, setGate] = useState<GateAnswer | null>(() => {
    if (formData.allergyGateAnswer) return formData.allergyGateAnswer;
    const saved = formData.proteinAllergies ?? [];
    if (saved.length > 0 && !saved.includes(Protein.NINGUNA)) return 'si';
    return null;
  });

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() =>
    buildInitialKeys(formData.proteinAllergies ?? [], formData.allergenKeys),
  );

  function handleGate(answer: GateAnswer): void {
    setGate(answer);
    if (answer === 'no') {
      // "No" means no known allergies (equivalent to Protein.NINGUNA). We stay
      // on the screen and just enable the CTA (no screen auto-advances).
      updateFormData({ allergyGateAnswer: 'no', proteinAllergies: [Protein.NINGUNA], allergenKeys: [] });
      return;
    }
    updateFormData({ allergyGateAnswer: 'si' });
  }

  function handleToggle(key: string): void {
    const next = new Set(selectedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setSelectedKeys(next);
    updateFormData({
      allergyGateAnswer: 'si',
      proteinAllergies: deriveAllergiesFromKeys(next),
      allergenKeys: Array.from(next),
    });
  }

  const compatibleRecipeCount = useMemo(() => {
    // Derive allergies from selectedKeys directly so the counter updates
    // in real-time (including vegetable allergens) without waiting for the
    // next formData flush.
    const allergies = Array.from(selectedKeys)
      .map((k) => {
        const opt = findOptionByKey(k);
        return opt ? opt.value : k;
      })
      .filter(Boolean);
    const currentAllergies = allergies.length > 0 ? (allergies as Array<Protein | string>) : [Protein.NINGUNA];
    const filtered = getAvailableRecipes(currentAllergies, recipes, {
      exclusionRules: config.exclusion_rules,
      pathologies: formData.pathologies,
      subPathologies: formData.subPathologies,
    });
    const isPuppy = formData.ageCategory === AgeCategory.CACHORRO;
    return filtered.filter((r) => {
      if (r.isExcluded) return false;
      if (!r.lifeStage) return true;
      return isPuppy ? r.lifeStage === 'puppy' : r.lifeStage === 'adult';
    }).length;
  }, [
    config.exclusion_rules,
    selectedKeys,
    formData.ageCategory,
    formData.pathologies,
    formData.subPathologies,
    recipes,
  ]);

  const handleOtrosClose = useCallback(() => {
    setShowOtros(false);
    setSearchTerm('');
  }, []);

  useEffect(() => {
    if (!showOtros) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') handleOtrosClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showOtros, handleOtrosClose]);

  const extendedSelected = EXTENDED_ALLERGENS.filter((a) => selectedKeys.has(a.key));

  const filteredExtendedProteinas = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    const items = EXTENDED_ALLERGENS.filter((a) => a.category === 'proteinas');
    if (!term) return items;
    return items.filter((a) => a.label.toLowerCase().includes(term));
  }, [searchTerm]);

  const filteredExtendedVegetales = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    const items = EXTENDED_ALLERGENS.filter((a) => a.category === 'vegetales');
    if (!term) return items;
    return items.filter((a) => a.label.toLowerCase().includes(term));
  }, [searchTerm]);

  const hasResults = filteredExtendedProteinas.length > 0 || filteredExtendedVegetales.length > 0;

  const recipeCountLabel =
    compatibleRecipeCount === 1
      ? t('p7.recipe_count_singular', '1 receta que le sentará genial')
      : t('p7.recipe_count_plural', '{count} recetas que le sentarán genial', { count: compatibleRecipeCount });

  const canContinue = gate === 'no' || (gate === 'si' && selectedKeys.size > 0);
  const missingReason =
    gate === null
      ? t('p7.missing_gate', 'Indica si {nombre} tiene alguna alergia o intolerancia', { nombre })
      : gate === 'si' && selectedKeys.size === 0
        ? t('p7.missing_selection', 'Selecciona al menos un ingrediente')
        : undefined;

  return (
    <FunnelLayout
      step={7}
      useTwoPhase
      onContinue={goToNextStep}
      continueDisabled={!canContinue}
      continueDisabledReason={missingReason}
      onContinueDisabledAttempt={() => setAttempted(true)}
      onBack={goToPreviousStep}
    >
      <div className="flex flex-col gap-4">
        {/* Gate question — con foto de raza para personalizar la pregunta */}
        <div className="flex flex-col items-center gap-3 text-center">
          <BreedPhoto breedKey={formData.raza} className="w-16 h-16 sm:w-20 sm:h-20" alt={`Foto de ${nombre}`} />

          <h1 className="font-fredoka text-xl sm:text-2xl font-semibold text-black leading-tight">
            {t('p7.title', '¿Hay algún ingrediente que {nombre} deba evitar?', { nombre })}
          </h1>

          <div className="inline-flex items-center justify-center gap-1.5 self-center">
            <p className="font-poppins text-sm text-black/60 leading-snug">
              {t('p7.subtitle', 'Marca solo alergias o intolerancias conocidas.')}
            </p>
            <button
              type="button"
              aria-label={t('p7.tooltip_toggle', 'Más información sobre alergias e intolerancias')}
              onClick={() => setShowTooltip((v) => !v)}
              className="shrink-0 w-5 h-5 rounded-full bg-[#008BBC]/15 text-[#008BBC] font-poppins text-xs font-semibold flex items-center justify-center hover:bg-[#008BBC]/25 transition-colors"
            >
              ⓘ
            </button>
          </div>

          {showTooltip && (
            <div className="relative w-full">
              <div className="bg-white border border-[#008BBC]/30 rounded-[14px] p-3 text-left shadow-md mt-1">
                <p className="font-poppins text-sm text-black/80 leading-snug">
                  {t('p7.tooltip_body', 'Alergia = reacción inmediata. Intolerancia = molestia digestiva continuada.')}
                </p>
                <button
                  type="button"
                  onClick={() => setShowTooltip(false)}
                  className="mt-2 font-poppins text-xs text-[#008BBC] underline"
                >
                  {t('p7.tooltip_close', 'Cerrar')}
                </button>
              </div>
            </div>
          )}

          {/* Gate pills — No first to reduce cognitive load (most dogs have no allergies) */}
          <div
            className={`grid grid-cols-2 gap-2 rounded-[18px] transition-all ${
              attempted && gate === null ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white p-2' : ''
            }`}
          >
            {(
              [
                { value: 'no', label: t('p7.gate_no', 'No') },
                { value: 'si', label: t('p7.gate_si', 'Sí') },
              ] as { value: GateAnswer; label: string }[]
            ).map(({ value, label }) => (
              <FunnelPillButton
                key={value}
                active={gate === value}
                onClick={() => handleGate(value)}
                className="py-3 px-8 text-base font-semibold"
              >
                {label}
              </FunnelPillButton>
            ))}
          </div>
        </div>

        {/* Allergen selection — only shown after "Sí" */}
        {gate === 'si' && (
          <>
            <div
              className={`grid grid-cols-3 gap-2.5 rounded-[18px] transition-all sm:grid-cols-3 sm:gap-3 ${
                attempted && selectedKeys.size === 0 ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white p-1' : ''
              }`}
            >
              {COMMON_PROTEINS.map((option) => {
                const checked = selectedKeys.has(option.key);
                return (
                  <button
                    key={option.key}
                    type="button"
                    aria-pressed={checked}
                    onClick={() => handleToggle(option.key)}
                    className={`relative bg-[#FAEDE1] rounded-[20px] flex flex-col items-center justify-center gap-0.5 pt-2.5 pb-1.5 px-1 transition-all aspect-square sm:aspect-auto sm:min-h-[110px] sm:gap-1 sm:px-4 sm:py-3 ${
                      checked ? 'border-[2.5px] border-[#008BBC]' : 'border-[2.5px] border-transparent'
                    }`}
                  >
                    {checked && (
                      <span
                        className="pointer-events-none absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#008BBC] text-[11px] font-bold leading-none text-white shadow-sm sm:right-2 sm:top-2"
                        aria-hidden="true"
                      >
                        ✓
                      </span>
                    )}
                    {option.icon ? (
                      <img
                        src={option.icon}
                        alt=""
                        aria-hidden="true"
                        className="w-14 h-14 sm:w-16 sm:h-16 object-contain"
                      />
                    ) : (
                      <span className="text-3xl sm:text-4xl h-14 sm:h-16 flex items-center justify-center">
                        {option.emoji}
                      </span>
                    )}
                    <span className="font-fredoka text-[10px] sm:text-base font-medium text-black leading-[1.1] text-center min-h-[1.2em]">
                      {t(`p7.protein.${option.key}`, option.label)}
                    </span>
                  </button>
                );
              })}
            </div>

            <div>
              <button
                type="button"
                aria-pressed={extendedSelected.length > 0}
                onClick={() => setShowOtros(true)}
                className={`relative flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full border-2 px-5 py-3 font-fredoka text-base font-semibold transition-all ${
                  extendedSelected.length > 0
                    ? 'bg-white border-[#008BBC] text-[#008BBC] shadow-[0_0_0_4px_rgba(0,139,188,0.15)]'
                    : 'bg-white border-[#E9E3D7] text-black/65 hover:border-[#90D1E8]'
                }`}
              >
                {extendedSelected.length > 0 && (
                  <span
                    className="pointer-events-none absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full bg-[#008BBC] text-[11px] font-bold leading-none text-white shadow-sm"
                    aria-hidden="true"
                  >
                    ✓
                  </span>
                )}
                <span className="text-lg leading-none" aria-hidden="true">
                  🔍
                </span>
                <span className="whitespace-nowrap leading-none">
                  {extendedSelected.length > 0
                    ? t('p7.otros_count', 'Otros ({count})', { count: extendedSelected.length })
                    : t('p7.otros', 'Otros')}
                </span>
              </button>
            </div>

            {extendedSelected.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {extendedSelected.map((a) => (
                  <span
                    key={a.key}
                    className="inline-flex items-center gap-1 bg-[#008BBC]/10 text-[#008BBC] text-xs font-poppins font-medium px-2.5 py-1 rounded-full"
                  >
                    {a.label}
                    <button
                      type="button"
                      aria-label={t('p7.remove_allergen_aria', 'Quitar {label}', { label: a.label })}
                      onClick={() => handleToggle(a.key)}
                      className="hover:text-red-500"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            <p data-testid="recipe-counter" className="font-poppins text-sm text-center text-[#008BBC] font-medium">
              {t('p7.recipe_counter', 'Con esta selección, {nombre} tiene {recipe_count_label}.', {
                nombre,
                count: compatibleRecipeCount,
                recipe_count_label: recipeCountLabel,
              })}
            </p>
          </>
        )}
      </div>

      {showOtros && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4"
          onClick={handleOtrosClose}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title-otros"
            className="bg-white rounded-[20px] p-5 max-w-sm w-full shadow-xl space-y-3 max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="modal-title-otros" className="font-fredoka text-lg font-medium text-black">
              {t('p7.otros_title', 'Otras alergias o intolerancias')}
            </h3>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-black/30" size={16} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('p7.otros_search', 'Buscar...')}
                autoFocus
                className="w-full pl-9 pr-4 py-2 bg-gray-100 rounded-xl font-poppins text-sm focus:outline-none focus:ring-2 focus:ring-[#008BBC]/30"
              />
            </div>

            <div className="overflow-y-auto flex-1 space-y-3">
              {filteredExtendedProteinas.length > 0 && (
                <div>
                  <p className="font-poppins text-xs font-semibold text-black/40 uppercase tracking-wide mb-1 px-1">
                    {t('p7.otros_section_proteinas', 'Proteínas')}
                  </p>
                  <div className="space-y-1">
                    {filteredExtendedProteinas.map((allergen) => {
                      const checked = selectedKeys.has(allergen.key);
                      return (
                        <button
                          key={allergen.key}
                          type="button"
                          aria-pressed={checked}
                          onClick={() => handleToggle(allergen.key)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left transition-all ${
                            checked
                              ? 'bg-[#008BBC]/10 border border-[#008BBC]'
                              : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                          }`}
                        >
                          <span className="font-poppins text-sm text-black">{allergen.label}</span>
                          {checked && <span className="text-[#008BBC] font-bold text-sm">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {filteredExtendedVegetales.length > 0 && (
                <div>
                  <p className="font-poppins text-xs font-semibold text-black/40 uppercase tracking-wide mb-1 px-1">
                    {t('p7.otros_section_vegetales', 'Ingredientes vegetales')}
                  </p>
                  <div className="space-y-1">
                    {filteredExtendedVegetales.map((allergen) => {
                      const checked = selectedKeys.has(allergen.key);
                      return (
                        <button
                          key={allergen.key}
                          type="button"
                          aria-pressed={checked}
                          onClick={() => handleToggle(allergen.key)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-left transition-all ${
                            checked
                              ? 'bg-[#008BBC]/10 border border-[#008BBC]'
                              : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                          }`}
                        >
                          <span className="font-poppins text-sm text-black">{allergen.label}</span>
                          {checked && <span className="text-[#008BBC] font-bold text-sm">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {!hasResults && (
                <p className="font-poppins text-sm text-black/40 text-center py-4">
                  {t('p7.no_results', 'No se encontraron resultados')}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handleOtrosClose}
              className="w-full py-2.5 bg-[#008BBC] rounded-xl font-fredoka text-white font-medium hover:bg-[#007aa8] transition-all"
            >
              {t('p7.otros_done', 'Hecho')}
            </button>
          </div>
        </div>
      )}
    </FunnelLayout>
  );
};

export default P7Alergias;
